import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useAddGroupMembers, useListGroupMemberships } from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, useMemo, type FC } from "react";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";
import { MemberSelectionList } from "@/components/settings/organization/groups/MemberSelectionList";

type AddGroupMembersDialogProps = {
    open: boolean;
    onClose: () => void;
    group: PlainMessage<Group>;
};

export const AddGroupMembersDialog: FC<AddGroupMembersDialogProps> = ({ open, onClose, group }) => {
    const { toast } = useToast();
    const { data: groupMemberships = [] } = useListGroupMemberships(group.id);

    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

    // Get current group member IDs for filtering
    const currentMemberIds = useMemo(() => {
        return new Set(
            groupMemberships.map((membership) => membership.subject?.id).filter((id): id is string => Boolean(id)),
        );
    }, [groupMemberships]);

    const addMembersMutation = useAddGroupMembers();

    const handleSubmit = useCallback(() => {
        if (selectedUserIds.size === 0) return;

        addMembersMutation.mutate(
            { groupId: group.id, userIds: Array.from(selectedUserIds) },
            {
                onSuccess: () => {
                    toast({
                        title: "Members added",
                        description: `${selectedUserIds.size} member${selectedUserIds.size === 1 ? "" : "s"} added to "${group.name}".`,
                    });

                    // Reset form
                    setSelectedUserIds(new Set());
                    onClose();
                },
                onError: (error) => {
                    toast({
                        title: "Failed to add members",
                        description: formatError(error),
                    });
                },
            },
        );
    }, [selectedUserIds, group, addMembersMutation, toast, onClose]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                // Reset form when dialog closes
                setSelectedUserIds(new Set());
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="add-group-members-dialog"
                data-track-location={TrackLocations.CreateProjectModal}
                className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>
                        <span className="truncate">Add members to {group.name}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Select organization members to add to this group. Members will gain access to group resources
                        and permissions.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
                    <MemberSelectionList
                        excludeUserIds={currentMemberIds}
                        selectedUserIds={selectedUserIds}
                        onChangeSelection={setSelectedUserIds}
                        showSearch={true}
                        className="flex min-h-0 flex-1 flex-col space-y-2"
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="add-members-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSubmit}
                        variant="primary"
                        disabled={selectedUserIds.size === 0 || addMembersMutation.isPending}
                        loading={addMembersMutation.isPending}
                        data-testid="add-members-dialog-add"
                        data-tracking-id="add-members-dialog-add"
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
